// @flow
import React from 'react';
import {fromJS, Set, List} from 'immutable';
import {stats} from 'lib/StatsUtil';
import TruncateTooltip from 'sg/TruncateTooltip/TruncateTooltip.react';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';

const columnToCSVHeaders = fromJS({
  teachers: {
    Teacher: {
      'Last Name': 'teacher_last_name',
      'First Name': 'teacher_first_name',
      Email: 'teacher_email'
    },
    Classes: 'count_of_school_classrooms',
    Subjects: 'subject_name',
    Students: 'count_of_school_students'
  },
  students: {
    Student: {
      'Last Name': 'student_last_name',
      'First Name': 'student_first_name',
      Email: 'student_email'
    },
    Username: 'student_username',
    Teachers: 'count_of_school_teachers',
    Classes: 'count_of_school_classrooms',
    Subjects: 'subject_name'
  },
  classes: {
    Class: 'classroom_name',
    Teachers: {
      'Last Name': 'teacher_last_name',
      'First Name': 'teacher_first_name',
      Email: 'teacher_email'
    },
    Subjects: 'subject_name',
    Students: 'count_of_school_students'
  },
  subjects: {
    Subject: 'subject_name',
    Classes: 'count_of_school_classrooms',
    Teachers: 'count_of_school_teachers',
    Students: 'count_of_school_students'
  },
  base: {
    Attempts: 'count_of_school_guesses',
    'Student Attempts': 'count_of_school_guesses',
    Assignments: 'count_of_school_assignments',
    Performance: {
      'Correct Guesses': 'count_of_school_correct_guesses',
      'Incorrect Guesses': 'count_of_school_incorrect_guesses'
    }
  }
});

function getCSVHeaders(table: string): Function {
  const csvColumns = columnToCSVHeaders.get(table).merge(columnToCSVHeaders.get('base'));

  function getCSVHeadersWithColumns(visibleColumns): string {
    const firstColumn = columnToCSVHeaders.get(table).keySeq().first();

    const formattedColumns = visibleColumns
      .toList()
      .unshift(firstColumn)
      .reduce((acc, value) => {
        if (typeof csvColumns.get(value) === 'string') {
          return acc.push(`${csvColumns.get(value)}=${value}`);
        }

        const nestedColumn = csvColumns.get(value).reduce((nestedAcc, nestedValue, nestedKey) => {
          return nestedAcc.push(`${nestedValue}=${nestedKey}`);
        }, List());

        return acc.concat(nestedColumn);
      }, List());

    return encodeURIComponent(formattedColumns.join(','));
  }

  return getCSVHeadersWithColumns;
}

function getCountOfSchoolClassroomsContent(
  content: Object,
  areSubjectsExpanded: boolean = false
): number {
  return areSubjectsExpanded && !content.getMeta().getSchoolSubjectBreakdown().isEmpty()
    ? content.getMeta().getSchoolSubjectBreakdown().get('count_of_school_classrooms', 0)
    : content.getMeta().getCountOfSchoolClassrooms();
}

function getCountOfSchoolTeachersContent(
  content: Object,
  areSubjectsExpanded: boolean = false
): number {
  return areSubjectsExpanded && !content.getMeta().getSchoolSubjectBreakdown().isEmpty()
    ? content.getMeta().getSchoolSubjectBreakdown().get('count_of_school_teachers', 0)
    : content.getMeta().getCountOfSchoolTeachers();
}

function getCountOfSchoolStudentsContent(
  content: Object,
  areSubjectsExpanded: boolean = false
): number {
  return areSubjectsExpanded && !content.getMeta().getSchoolSubjectBreakdown().isEmpty()
    ? content.getMeta().getSchoolSubjectBreakdown().get('count_of_school_students', 0)
    : content.getMeta().getCountOfSchoolStudents();
}

function getCountOfSchoolAssignmentsContent(
  content: Object,
  areSubjectsExpanded: boolean = false
): number {
  return areSubjectsExpanded && !content.getMeta().getSchoolSubjectBreakdown().isEmpty()
    ? content.getMeta().getSchoolSubjectBreakdown().get('count_of_school_assignments', 0)
    : content.getMeta().getCountOfSchoolAssignments();
}

function getSchoolSubjectsContent(
  content: Object,
  areSubjectsExpanded: boolean = false
): React.Element<*> {
  if (areSubjectsExpanded && !content.getMeta().getSchoolSubjectBreakdown().isEmpty()) {
    return content.getMeta().getSchoolSubjectBreakdown().get('subject_name', '');
  }

  const subjects = Set(
    content
      .getMeta()
      .getSchoolSubjectNames()
      .split(/, (?!and )(?!Politics, and)/)
  )
    .toList()
    .sort();
  return (
    <TruncateTooltip
      className='usage-report__subjects-tooltip'
      content={subjects}
      position='bottom'
      onHover
    />
  );
}

function getStudentAttemptsContent(content: Object, areSubjectsExpanded: boolean = false): number {
  return areSubjectsExpanded && !content.getMeta().getSchoolSubjectBreakdown().isEmpty()
    ? content.getMeta().getSchoolSubjectBreakdown().get('count_of_school_guesses', 0)
    : content.getMeta().getCountOfSchoolGuesses() ?? 0;
}

function getPerformanceBarContent(
  content: Object,
  areSubjectsExpanded: boolean = false
): React.Element<*> {
  let correctGuesses;
  let incorrectGuesses;
  const contentMeta = content.getMeta();

  if (areSubjectsExpanded && !contentMeta.getSchoolSubjectBreakdown().isEmpty()) {
    correctGuesses = contentMeta
      .getSchoolSubjectBreakdown()
      .get('count_of_school_correct_guesses', 0);
    incorrectGuesses = contentMeta
      .getSchoolSubjectBreakdown()
      .get('count_of_school_incorrect_guesses', 0);
  } else {
    correctGuesses = contentMeta.getCountOfSchoolCorrectGuesses();
    incorrectGuesses = contentMeta.getCountOfSchoolIncorrectGuesses();
  }

  const totalGuesses = correctGuesses + incorrectGuesses;
  const performanceData = fromJS([
    {
      fillColorClass: 'usage-report__performance--correct',
      fillPercentage: stats.percentage(correctGuesses, totalGuesses),
      keyLabel: 'Correct Attempts'
    },
    {
      fillColorClass: 'usage-report__performance--incorrect',
      fillPercentage: stats.percentage(incorrectGuesses, totalGuesses),
      keyLabel: 'Incorrect Attempts'
    }
  ]);
  return (
    <div className='usage-report__performance'>
      <small>
        {correctGuesses} / {totalGuesses}
      </small>
      <FlatBarChart inline data={performanceData} />
    </div>
  );
}

export {
  getCSVHeaders,
  getCountOfSchoolClassroomsContent,
  getCountOfSchoolTeachersContent,
  getCountOfSchoolStudentsContent,
  getCountOfSchoolAssignmentsContent,
  getSchoolSubjectsContent,
  getStudentAttemptsContent,
  getPerformanceBarContent
};
